/**
 * Ultra-optimized Cloudinary helpers for KIRAQ
 *
 * Goals:
 * - Fast first paint
 * - Sharp luxury-fashion imagery
 * - Minimal bandwidth
 * - Proper retina handling
 */

const CLOUDINARY_REGEX = /res\.cloudinary\.com/;

function stripTransformations(url) {
  const parts = url.split('/upload/');
  if (parts.length !== 2) return url;

  const afterUpload = parts[1];
  const pathSegments = afterUpload.split('/');

  const hasTransform =
  /^(f_|q_|w_|h_|c_|g_|dpr_|fl_)/.test(pathSegments[0]);

  const resource = hasTransform
    ? pathSegments.slice(1).join('/')
    : afterUpload;

  return `${parts[0]}/upload/${resource}`;
}

export function cloudinaryUrl(
  url,
  {
    width,
    height,
    quality = 'auto:good',
    crop = 'fill',
  } = {}
) {
  if (!url || !CLOUDINARY_REGEX.test(url)) return url;

  const clean = stripTransformations(url);

  const parts = clean.split('/upload/');
  if (parts.length !== 2) return url;

  const transforms = [
    'f_auto',
    `q_${quality}`,
    'dpr_auto',
  ];

  if (width) transforms.push(`w_${width}`);
  if (height) transforms.push(`h_${height}`);

  if (width || height) {
    transforms.push(`c_${crop}`);
    transforms.push('g_auto');
  }

  /**
   * Progressive rendering:
   * Huge performance gain on slower mobile.
   */
  transforms.push('fl_progressive');

  return `${parts[0]}/upload/${transforms.join(',')}/${parts[1]}`;
}

/**
 * Generic responsive srcSet helper
 */
export function cloudinarySrcSet(
  url,
  widths = [320, 480, 640, 960]
) {
  if (!url || !CLOUDINARY_REGEX.test(url)) return '';

  return widths
    .map((w) =>
      `${cloudinaryUrl(url, {
        width: w,
        quality: 'auto:good',
      })} ${w}w`
    )
    .join(', ');
}

/**
 * Product card thumbnail.
 *
 * REAL rendered size:
 * Mobile ≈ 180–220px
 * Desktop ≈ 280–340px
 *
 * Don't overserve.
 */
export function productThumb(url, displayWidth = 360) {
  return cloudinaryUrl(url, {
    width: displayWidth,
    height: Math.round(displayWidth * 4 / 3),
    quality: 'auto:good',
  });
}

/**
 * Lean srcset.
 *
 * Previous widths were too large.
 */
export function productThumbSrcSet(url) {
  const widths = [220, 320, 420, 520];

  return widths
    .map((w) => {
      const h = Math.round(w * 4 / 3);

      return `${cloudinaryUrl(url, {
        width: w,
        height: h,
        quality: 'auto:good',
      })} ${w}w`;
    })
    .join(', ');
}